import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../api/supabase'
import SearchableSelect from '../../../components/SearchableSelect'
import type { PricingLane, PricingCategory } from './types'

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

// Same strip-special-chars approach as ShipDetailsTab.tsx's searchPorts -- port names/codes
// never legitimately contain PostgREST or()/ilike syntax characters.
function escapeIlikeTerm(term: string): string {
  return term.trim().replace(/[%_,()]/g, '')
}

async function searchPorts(term: string) {
  const cleaned = escapeIlikeTerm(term)
  let query = supabase.from('ports').select('id, code, name, type, un_locode').eq('is_active', true).order('code').limit(50)
  if (cleaned) query = query.or(`name.ilike.%${cleaned}%,code.ilike.${cleaned}%,un_locode.ilike.${cleaned}%`)
  const { data, error } = await query
  if (error) { console.error('[searchPorts] Error:', error); return [] }
  return (data || []).map(p => ({ id: p.id, label: `${p.un_locode || p.code} — ${p.name}`, code: p.code, name: p.name }))
}

interface PortOption { id: string; label: string }

type FormState = Omit<PricingLane, 'id'> & { id?: string }

function emptyForm(defaultCategoryId: string): FormState {
  return { name: '', category_id: defaultCategoryId, origin_port_id: null, destination_port_id: null, origin_city: '', destination_city: '', is_active: true }
}

export default function LanesTab() {
  const [rows, setRows] = useState<PricingLane[]>([])
  const [categories, setCategories] = useState<PricingCategory[]>([])
  const [portLabels, setPortLabels] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState<FormState | null>(null)
  const [originPort, setOriginPort] = useState<PortOption | null>(null)
  const [destPort, setDestPort] = useState<PortOption | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const [{ data: lanes }, { data: cats }] = await Promise.all([
      supabase.from('pricing_lanes').select('*').order('name'),
      supabase.from('pricing_categories').select('*').order('display_order'),
    ])
    const laneRows = (lanes as PricingLane[]) || []
    setRows(laneRows)
    setCategories((cats as PricingCategory[]) || [])

    // Resolve port labels for the list view in one batch, rather than N+1 lookups per row.
    const portIds = Array.from(new Set(laneRows.flatMap(l => [l.origin_port_id, l.destination_port_id]).filter((id): id is string => !!id)))
    if (portIds.length > 0) {
      const { data: ports } = await supabase.from('ports').select('id, code, name, un_locode').in('id', portIds)
      const labels: Record<string, string> = {}
      for (const p of ports || []) labels[p.id] = `${p.un_locode || p.code} — ${p.name}`
      setPortLabels(labels)
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openAdd = () => {
    setForm(emptyForm(categories[0]?.id || ''))
    setOriginPort(null); setDestPort(null)
    setError(null); setModal(true)
  }
  const openEdit = async (r: PricingLane) => {
    setForm({ ...r })
    setOriginPort(r.origin_port_id ? { id: r.origin_port_id, label: portLabels[r.origin_port_id] || '' } : null)
    setDestPort(r.destination_port_id ? { id: r.destination_port_id, label: portLabels[r.destination_port_id] || '' } : null)
    setError(null); setModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form) return
    if (!form.name.trim() || !form.category_id) { setError('Name and Category are required.'); return }
    setSaving(true); setError(null)
    try {
      const { id, ...rest } = form
      const { error: err } = id
        ? await supabase.from('pricing_lanes').update(rest).eq('id', id)
        : await supabase.from('pricing_lanes').insert(rest)
      if (err) throw err
      setModal(false)
      load()
    } catch (err: any) {
      setError(err?.message || 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (r: PricingLane) => {
    await supabase.from('pricing_lanes').update({ is_active: !r.is_active }).eq('id', r.id)
    load()
  }

  const categoryCode = (id: string) => categories.find(c => c.id === id)?.code || '—'

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">Port pairs (or city pairs, for truck) a rate card can be scoped to -- leave a rate card's lane blank to mean "all lanes".</p>
        <button onClick={openAdd} disabled={categories.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap ml-4">
          + New Lane
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-400">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                {['Name', 'Category', 'Origin', 'Destination', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs">{categoryCode(r.category_id)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{(r.origin_port_id && portLabels[r.origin_port_id]) || r.origin_city || <span className="text-gray-400">all</span>}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{(r.destination_port_id && portLabels[r.destination_port_id]) || r.destination_city || <span className="text-gray-400">all</span>}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {r.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 space-x-3">
                    <button onClick={() => openEdit(r)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                    <button onClick={() => toggleActive(r)} className={`text-xs font-medium ${r.is_active ? 'text-red-500 hover:underline' : 'text-green-600 hover:underline'}`}>
                      {r.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No lanes found -- rate cards work fine without any (means "all lanes")</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal && form && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{form.id ? 'Edit Lane' : 'New Lane'}</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <input className={inputCls} value={form.name} onChange={e => setForm(f => f && ({ ...f, name: e.target.value }))} placeholder='e.g. "SHA-FXT (Sea)"' />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category *</label>
                <select className={inputCls} value={form.category_id} onChange={e => setForm(f => f && ({ ...f, category_id: e.target.value }))}>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name_en} ({c.code})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Origin Port <span className="text-gray-400 font-normal">(blank = all origins)</span></label>
                <SearchableSelect
                  options={[]}
                  value={form.origin_port_id || ''}
                  onChange={id => { setForm(f => f && ({ ...f, origin_port_id: id || null })) }}
                  onSearch={searchPorts}
                  selectedOption={originPort}
                  placeholder="Search ports…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Origin City <span className="text-gray-400 font-normal">(non-port lanes, e.g. truck)</span></label>
                <input className={inputCls} value={form.origin_city || ''} onChange={e => setForm(f => f && ({ ...f, origin_city: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destination Port <span className="text-gray-400 font-normal">(blank = all UK)</span></label>
                <SearchableSelect
                  options={[]}
                  value={form.destination_port_id || ''}
                  onChange={id => { setForm(f => f && ({ ...f, destination_port_id: id || null })) }}
                  onSearch={searchPorts}
                  selectedOption={destPort}
                  placeholder="Search ports…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destination City</label>
                <input className={inputCls} value={form.destination_city || ''} onChange={e => setForm(f => f && ({ ...f, destination_city: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => f && ({ ...f, is_active: e.target.checked }))} className="h-4 w-4 text-blue-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
